import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useNavigate } from "react-router-dom";

import UserProfile from '../../components/User';
import AccessController from '../../components/access_controller';

/**
 * Home is a launcher, used standing up in a kitchen, usually one-handed.
 *
 * It is laid out as a two-column grid of large tiles rather than a single
 * column of fixed-width buttons: the old layout used a 175px button on a 375px
 * screen, wasting half the width and pushing the last actions below the fold.
 *
 * Actions are grouped by the job they belong to, so the eye can skip to the
 * right group instead of reading eight similar labels. Colour marks the two
 * subtractive actions (usage, wastage) - it is not decoration.
 */
const GROUPS = [
    {
        title: 'Daily inventory',
        actions: [
            { label: 'Fill Inventory', path: '/inventory/add/status' },
            { label: 'Add Preparations', path: '/inventory/add/preparation' },
            { label: 'Add Usage', path: '/inventory/add/usage/', tone: 'warning' },
            { label: 'Add Wastage', path: '/inventory/add/wastage/', tone: 'error' },
        ],
    },
    {
        title: 'Bills',
        actions: [
            { label: 'Add Bill', path: '/bill/add/' },
            { label: 'View Bills', path: '/bill/view' },
        ],
    },
    {
        title: 'Orders and performance',
        actions: [
            { label: 'View Orders', path: '/orders/view' },
            { label: 'Performance', path: '/analytics' },
        ],
    },
];

function ActionTile(props) {
    const { label, tone, onClick } = props;
    return (
        <ButtonBase
            onClick={onClick}
            aria-label={label}
            sx={{
                width: '100%', borderRadius: 2, textAlign: 'left',
                // Comfortably tappable without aiming.
                minHeight: 72,
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: '100%', height: '100%', minHeight: 72,
                    display: 'flex', alignItems: 'center',
                    px: 2, py: 1.5, borderRadius: 2,
                    borderColor: tone ? `${tone}.main` : 'divider',
                    borderLeftWidth: tone ? 4 : 1,
                    transition: 'background-color 120ms',
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                    {label}
                </Typography>
            </Paper>
        </ButtonBase>
    );
}

export default function HomePage() {
    const navigate = useNavigate();
    const username = UserProfile.getName();

    const groups = GROUPS.map((group) => {
        if (group.title !== 'Bills' || !UserProfile.isManager()) return group;
        // Managers also get inventory oversight, alongside the bills they review.
        return {
            ...group,
            actions: [...group.actions, { label: 'View Inventory', path: '/inventory/view' }],
        };
    });

    return (
        <AccessController onlyAdmin={false}>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Store Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Welcome {username}
                    </Typography>
                </Box>

                {groups.map((group) => (
                    <Box key={group.title} sx={{ mb: 3 }}>
                        <Typography
                            variant="overline"
                            sx={{ color: 'text.secondary', letterSpacing: '.08em' }}
                        >
                            {group.title}
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                            {group.actions.map((action) => (
                                // Two per row on a phone, four on a tablet and up.
                                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={action.label}>
                                    <ActionTile
                                        label={action.label}
                                        tone={action.tone}
                                        onClick={() => navigate(action.path)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ))}
            </Box>
        </AccessController>
    );
}
